/*
 * NC Popup — <nc-popup>: captación con diálogo modal nativo (focus trap, Escape, aria-modal e inert del resto de la página
 * los aporta <dialog>.showModal()). Vanilla, sin librerías.
 *
 *  · Formulario: el `customer` nativo de Shopify (crea/actualiza el cliente con la etiqueta de captación). Se envía por
 *    fetch a la misma URL del formulario para no recargar la página; si Shopify pide verificación (/challenge) o algo no
 *    cuadra, se cae al envío nativo (recarga) y el estado de éxito lo pinta el servidor (form.posted_successfully?).
 *  · Cuándo abre: tras `data-delay` segundos, y solo si no hay nada más abierto ni el cliente está decidiendo una compra
 *    (drawer del carrito, menú, búsqueda, modales, builder 3×2, selector Oro/Plata, ATC reciente). Nunca en /cart ni checkout.
 *  · Frecuencia: localStorage (con cookie de respaldo). Cierre → `data-days` días; suscrito → 90 días.
 *  · Imagen: vive en un <template>; se instancia en warm() (poco antes de abrir) para no competir con el LCP.
 *  · Theme Editor: no abre solo ni escribe estado; se previsualiza al seleccionar la sección.
 */
const STORE_KEY = 'nc_popup_v1';
const LEGACY_KEY = 'northcrafter_popup_state';
const SUBSCRIBED_DAYS = 90;
const DAY_MS = 86400000;
const QUIET_MS = 20000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/;

function readState() {
  let raw = null;
  try { raw = localStorage.getItem(STORE_KEY); } catch (error) { /* almacenamiento bloqueado */ }
  if (!raw) {
    const match = document.cookie.match(/(?:^|; )nc_popup_v1=([^;]*)/);
    raw = match ? decodeURIComponent(match[1]) : null;
  }
  if (raw) {
    try { return JSON.parse(raw); } catch (error) { /* valor corrupto */ }
  }
  try {
    // Estado del popup anterior (misma tienda): un cliente ya suscrito no debe volver a verlo.
    if (localStorage.getItem(LEGACY_KEY) === 'subscribed') return { s: 'subscribed', t: Date.now() };
  } catch (error) { /* noop */ }
  return null;
}

function writeState(state, days) {
  const value = JSON.stringify({ s: state, t: Date.now() });
  try { localStorage.setItem(STORE_KEY, value); } catch (error) { /* noop */ }
  document.cookie = `nc_popup_v1=${encodeURIComponent(value)}; max-age=${Math.round(days * 86400)}; path=/; SameSite=Lax`;
}

class NcPopup extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.dialog = this.querySelector('[data-ncpop-dialog]');
    this.panel = this.querySelector('[data-ncpop-panel]');
    this.form = this.querySelector('[data-ncpop-form]');
    this.input = this.querySelector('[data-ncpop-input]');
    this.error = this.querySelector('[data-ncpop-error]');
    this.submit = this.querySelector('[data-ncpop-submit]');
    this.copyBtn = this.querySelector('[data-ncpop-copy]');
    this.status = this.querySelector('[data-ncpop-status]');
    this.img = null; // se crea en warm() a partir del <template> (no se descarga con la página)
    this.delay = Math.max(3, Number(this.dataset.delay) || 6) * 1000;
    this.days = Math.max(1, Number(this.dataset.days) || 7);
    this.design = this.dataset.designMode === 'true' || !!(window.Shopify && window.Shopify.designMode);
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.quietUntil = 0;
    this.suppressed = false;
    this.timer = 0;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.querySelectorAll('[data-ncpop-close]').forEach((el) => el.addEventListener('click', () => this.close('dismissed'), { signal }));
    this.querySelector('[data-ncpop-backdrop]')?.addEventListener('click', () => this.close('dismissed'), { signal });
    this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.close('dismissed'); }, { signal });
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close('dismissed'); }, { signal });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), { signal });
    this.input?.addEventListener('input', () => this.setError(''), { signal });
    this.copyBtn?.addEventListener('click', () => this.copyCode(), { signal });

    if (this.design) {
      // Theme Editor: se previsualiza al seleccionar la sección; nunca reaparece solo mientras se edita.
      document.addEventListener('shopify:section:select', (event) => { if (this.contains(event.target) || event.target.contains(this)) this.open(true); }, { signal });
      document.addEventListener('shopify:section:deselect', () => this.close('silent'), { signal });
      return;
    }

    // Retorno del envío nativo (fallback): el servidor ya pintó el éxito o el error.
    const posted = this.dataset.posted;
    if (posted && location.hash === '#ncpop-form') {
      if (posted === 'success') { this.showSuccess(false); }
      this.open(true);
      return;
    }
    if (!this.eligible()) return;

    // Señales de que el cliente está decidiendo una compra: se retrasa la apertura.
    document.addEventListener('click', (event) => this.onInteraction(event), { capture: true, passive: true, signal });
    document.addEventListener('theme-drawer:close', () => { this.quietUntil = Date.now() + 8000; }, { signal });
    document.addEventListener('visibilitychange', () => { if (!document.hidden && this.waiting) this.attempt(); }, { signal });

    const start = () => {
      this.timer = setTimeout(() => this.attempt(), this.delay);
      // La imagen del popup se descarga poco antes de necesitarse (no compite con el LCP del Home).
      setTimeout(() => this.warm(), Math.max(1200, this.delay - 2500));
    };
    if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true, signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.timer);
    clearTimeout(this.copyTimer);
    document.documentElement.classList.remove('nc-popup-lock');
    this.ready = false;
  }

  eligible() {
    if (/^\/(cart|checkouts?|account|password)(\/|$)/.test(location.pathname)) return false;
    const state = readState();
    if (!state) return true;
    const days = state.s === 'subscribed' ? SUBSCRIBED_DAYS : this.days;
    return Date.now() - state.t > days * DAY_MS;
  }

  onInteraction(event) {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    if (el.closest('nc-bundle, nc-bundle-picker')) { this.suppressed = true; return; }
    if (el.closest('[data-nc-option-value], .nc-pdp__option-value, .nc-pdp__atc, [name="add"], product-form-component, .cart-drawer-cross-sell, .nc-pdp__sticky-btn')) {
      this.quietUntil = Date.now() + QUIET_MS;
    }
  }

  blocked() {
    if (this.suppressed || Date.now() < this.quietUntil) return true;
    if (document.hidden) return true;
    if (document.querySelector('dialog[open], theme-drawer[open]')) return true;
    const root = document.documentElement;
    if (root.classList.contains('ncfh-lock') || root.classList.contains('nc-popup-lock')) return true;
    if (document.querySelector('nc-floating-header[data-mega-open]')) return true;
    const active = document.activeElement;
    if (active && active !== document.body && active.matches('input, textarea, select, [contenteditable="true"]')) return true;
    return false;
  }

  attempt() {
    if (this.opened || !this.ready) return;
    if (document.hidden) { this.waiting = true; return; }
    this.waiting = false;
    if (this.blocked() || !this.eligible()) {
      // Sin apilar overlays: se vuelve a intentar cuando se libere la pantalla.
      this.timer = setTimeout(() => this.attempt(), 1200);
      return;
    }
    this.open(false);
  }

  warm() {
    if (this.img) return;
    const holder = this.querySelector('[data-ncpop-media]');
    const tpl = holder && holder.querySelector('template');
    if (!tpl) return;
    holder.appendChild(tpl.content.cloneNode(true));
    this.img = holder.querySelector('.ncpop__img');
    if (this.img) this.img.loading = 'eager';
  }

  imageReady() {
    const img = this.img;
    if (!img || img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, 1200);
    });
  }

  async open(force) {
    if (this.opened) return;
    this.opened = true;
    this.warm();
    await this.imageReady();
    if (!this.ready) return;
    if (!force && this.blocked()) {
      this.opened = false;
      this.timer = setTimeout(() => this.attempt(), 1200);
      return;
    }
    this.previousFocus = document.activeElement;
    document.documentElement.classList.add('nc-popup-lock');
    if (!this.dialog.open) this.dialog.showModal();
    requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('data-open', '')));
    const heading = this.querySelector(this.getAttribute('data-state') === 'success' ? '[data-ncpop-success-title]' : '[data-ncpop-title]');
    // El foco va al título (no al correo) para que el teclado del móvil no se abra solo.
    heading?.focus({ preventScroll: true });
  }

  close(reason) {
    if (!this.opened) return;
    this.opened = false;
    if (reason !== 'silent' && !this.design) {
      const subscribed = this.getAttribute('data-state') === 'success' || this.subscribed;
      writeState(subscribed ? 'subscribed' : 'dismissed', subscribed ? SUBSCRIBED_DAYS : this.days);
    }
    this.removeAttribute('data-open');
    const ms = this.reduced.matches ? 0 : (parseFloat(getComputedStyle(this.panel).transitionDuration) || 0) * 1000 + 80;
    const finish = () => {
      if (this.opened) return; // se reabrió durante la animación
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('nc-popup-lock');
      const back = this.previousFocus;
      this.previousFocus = null;
      if (back && document.contains(back) && back.focus) back.focus({ preventScroll: true });
    };
    if (ms) setTimeout(finish, ms); else finish();
  }

  setError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
    if (message) {
      this.input.setAttribute('aria-invalid', 'true');
      this.input.setAttribute('aria-describedby', this.error.id);
    } else {
      this.input.removeAttribute('aria-invalid');
      this.input.removeAttribute('aria-describedby');
    }
  }

  setBusy(on) {
    this.submit.disabled = on;
    this.submit.setAttribute('aria-busy', String(on));
    const label = this.submit.querySelector('[data-ncpop-submit-label]');
    if (label) {
      if (on) { label.dataset.idle = label.textContent; label.textContent = this.dataset.labelSending || 'Enviando…'; }
      else if (label.dataset.idle) label.textContent = label.dataset.idle;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submit.disabled) return; // sin doble envío
    const value = this.input.value.trim();
    if (!value) { this.setError(this.dataset.errorEmpty || 'Ingresa tu correo.'); this.input.focus(); return; }
    if (!EMAIL_RE.test(value)) { this.setError(this.dataset.errorInvalid || 'Ingresa un correo válido.'); this.input.focus(); return; }
    this.setError('');
    this.setBusy(true);
    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        redirect: 'follow'
      });
      if (response.url.includes('/challenge') || response.url.includes('/throttle')) {
        // Verificación de Shopify: necesita la navegación completa.
        this.form.submit();
        return;
      }
      // Éxito = Shopify redirige de vuelta a la página con ?customer_posted=true (los errores se quedan en /contact).
      if (response.ok && (/customer_posted=true/.test(response.url) || (response.redirected && !/\/contact/.test(response.url)))) {
        this.showSuccess(true);
        return;
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const serverError = doc.querySelector('[data-ncpop-error]');
      const message = serverError && serverError.textContent.trim();
      this.setError(message || this.dataset.errorGeneric || 'No pudimos registrarte. Intenta de nuevo.');
      this.setBusy(false);
      this.input.focus();
    } catch (error) {
      this.setError(this.dataset.errorGeneric || 'No pudimos registrarte. Intenta de nuevo.');
      this.setBusy(false);
    }
  }

  showSuccess(move) {
    this.subscribed = true;
    this.setAttribute('data-state', 'success');
    this.querySelector('[data-ncpop-form-state]').hidden = true;
    this.querySelector('[data-ncpop-success-state]').hidden = false;
    if (!this.design) writeState('subscribed', SUBSCRIBED_DAYS);
    if (move) this.querySelector('[data-ncpop-success-title]')?.focus({ preventScroll: true });
  }

  legacyCopy(code) {
    // Respaldo: textarea temporal dentro del diálogo (el resto de la página está inerte).
    const area = document.createElement('textarea');
    area.value = code;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    this.dialog.appendChild(area);
    area.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch (error) { ok = false; }
    area.remove();
    return ok;
  }

  async copyCode() {
    const code = this.dataset.code || '';
    if (!code) return;
    let ok = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        // Sin bloquear si el navegador deja la promesa pendiente (permiso).
        await Promise.race([navigator.clipboard.writeText(code), new Promise((_, reject) => setTimeout(reject, 1200))]);
        ok = true;
      } catch (error) { ok = false; }
    }
    if (!ok) ok = this.legacyCopy(code);
    if (!ok) {
      // Último recurso: deja el código seleccionado para copiarlo a mano.
      const codeEl = this.querySelector('.ncpop__code');
      if (codeEl) window.getSelection()?.selectAllChildren(codeEl);
    }
    const label = this.copyBtn.querySelector('[data-ncpop-copy-label]');
    if (!label) return;
    clearTimeout(this.copyTimer);
    if (!label.dataset.idle) label.dataset.idle = label.textContent;
    label.textContent = ok ? (this.dataset.labelCopied || 'Copiado ✓') : (this.dataset.labelCopyFail || 'Selecciona el código');
    if (this.status) this.status.textContent = ok ? (this.dataset.labelCopied || 'Copiado') : '';
    this.copyTimer = setTimeout(() => { label.textContent = label.dataset.idle; if (this.status) this.status.textContent = ''; }, 1800);
  }
}

if (!customElements.get('nc-popup')) customElements.define('nc-popup', NcPopup);
